"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseLocalizedNumber = void 0;
/**
 * Smart number parser that auto-detects locale format (English vs German).
 *
 * Detection rules:
 * - Both `,` and `.` present → last one is the decimal separator
 * - Multiple dots only → dots are thousands separators
 * - Multiple commas only → commas are thousands separators
 * - Single separator with exactly 3 trailing digits (e.g. "1.000" or "1,000")
 *   → ambiguous: use `locale` hint to decide (defaults to German)
 * - Single dot otherwise → dot is decimal
 * - Single comma otherwise → comma is decimal
 *
 * @param value - Raw input value
 * @param locale - BCP 47 locale hint for ambiguous cases (default: 'de')
 *                 Frontend can pass `navigator.language`, backend uses the default.
 * @returns parsed number or null if unparseable
 */
const parseLocalizedNumber = (value, locale = 'de') => {
    if (value === null || value === undefined || value === '')
        return null;
    if (typeof value === 'number')
        return value;
    const str = String(value).trim();
    if (str === '')
        return null;
    // Strip whitespace / non-breaking spaces used as thousands separators
    const cleaned = str.replace(/[\s\u00A0]/g, '');
    const dotCount = (cleaned.match(/\./g) || []).length;
    const commaCount = (cleaned.match(/,/g) || []).length;
    let normalized;
    if (dotCount > 0 && commaCount > 0) {
        // Both present → the LAST separator is the decimal mark
        const lastDot = cleaned.lastIndexOf('.');
        const lastComma = cleaned.lastIndexOf(',');
        if (lastDot > lastComma) {
            // English: 1,234.56 → dot is decimal
            normalized = cleaned.replace(/,/g, '');
        }
        else {
            // German: 1.234,56 → comma is decimal
            normalized = cleaned.replace(/\./g, '').replace(',', '.');
        }
    }
    else if (dotCount > 1) {
        // Multiple dots, no commas → dots are thousands: 1.000.000
        normalized = cleaned.replace(/\./g, '');
    }
    else if (commaCount > 1) {
        // Multiple commas, no dots → commas are thousands: 1,000,000
        normalized = cleaned.replace(/,/g, '');
    }
    else if (dotCount === 1 && commaCount === 0) {
        // Single dot, no comma
        // Ambiguous case: "1.000" could be 1000 (German thousands) or 1.0 (English decimal)
        // But "0.500" is clearly decimal (not thousands), so exclude leading zeros
        if (/^-?[1-9]\d{0,2}\.\d{3}$/.test(cleaned)) {
            normalized = resolveAmbiguous(cleaned, '.', locale);
        }
        else {
            // Not ambiguous → dot is decimal: "2.2", "3.14", "0.5", "12.34", "0.500"
            normalized = cleaned;
        }
    }
    else if (commaCount === 1 && dotCount === 0) {
        // Single comma, no dot
        if (/^-?[1-9]\d{0,2},\d{3}$/.test(cleaned)) {
            normalized = resolveAmbiguous(cleaned, ',', locale);
        }
        else {
            // Not ambiguous → comma is decimal: "2,2", "3,14", "0,5", "0,500"
            normalized = cleaned.replace(',', '.');
        }
    }
    else {
        // No separators at all
        normalized = cleaned;
    }
    // Reject partially-numeric strings like "12abc" — must be a valid number in its entirety
    if (!/^-?\d+(\.\d+)?$/.test(normalized))
        return null;
    const result = parseFloat(normalized);
    return Number.isNaN(result) ? null : result;
};
exports.parseLocalizedNumber = parseLocalizedNumber;
/**
 * Resolve ambiguous patterns like "1.000" or "1,000" using locale hint.
 * German locale → separator is thousands; English → separator is decimal.
 */
const resolveAmbiguous = (cleaned, separator, locale) => {
    const isGermanLike = locale.startsWith('de');
    if (separator === '.') {
        // "1.000" → German: 1000, English: 1.0
        return isGermanLike ? cleaned.replace('.', '') : cleaned;
    }
    // "1,000" → German: 1.0, English: 1000
    return isGermanLike ? cleaned.replace(',', '.') : cleaned.replace(',', '');
};
